/**
 * Normalizes error responses from the backend for Custom Reports into business-friendly messages.
 * 
 * @param {Error|any} err The error object caught in a try/catch or from an axios response
 * @param {string} fallbackMessage The default message to show if the error cannot be parsed
 * @returns {string} The normalized, user-friendly error message
 */
export function normalizeReportError(err, fallbackMessage = 'An unexpected error occurred.') {
    if (!err) return fallbackMessage;

    // Axios or API response error format
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // Fastapi detail can be a string or an object with a message, or an array of validation errors
    let backendMessage = null;
    if (typeof detail === 'string') {
        backendMessage = detail;
    } else if (detail?.message) {
        backendMessage = detail.message;
    } else if (Array.isArray(detail) && detail.length > 0) {
        backendMessage = detail[0]?.msg;
    }

    if (backendMessage && status) {
        // Business logic errors from backend (400, 403, 503) usually have good descriptions now
        if (status === 400 || status === 403 || status === 503) {
            return backendMessage;
        }
        // Mask other 500s or unexpected server errors
        if (status >= 500) {
            return 'The reporting server encountered an unexpected error. Please try again later.';
        }
        return backendMessage;
    }

    // Network error (no response)
    if (err.message === 'Network Error' || !err.response) {
        return 'Unable to connect to the reporting service. Please check your connection and try again.';
    }

    return err.message || fallbackMessage;
}
